import { CFormFeedback } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilCloudUpload } from '@coreui/icons'
import React, { useEffect, useRef, useState } from 'react'

export const SUPPORTED_IMAGE_FORMATS = ['image/jpeg', 'image/png', 'image/webp']
export const MAX_IMAGE_SIZE = 2 * 1024 * 1024 // 2MB

/**
 * Shared drag-and-drop image picker. Use this instead of a bare `<CFormInput type="file" />`
 * in every add/edit form that uploads an image.
 *
 * Preview source, in order: `previewUrl` (caller-managed object URL) -> `file` (a File in form
 * state, whose object URL this component creates and revokes) -> `existingUrl` (the image
 * already saved on the record).
 */
const ImageDropzone = ({
  onChange,
  error,
  file = null,
  previewUrl,
  existingUrl,
  accept = 'image/jpeg,image/png,image/webp',
  title,
  hint = 'JPG, PNG or WEBP up to 2MB',
  minHeight = 150,
  previewSize = 80,
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [filePreviewUrl, setFilePreviewUrl] = useState(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (!(file instanceof File)) {
      setFilePreviewUrl(null)
      return
    }
    const objectUrl = URL.createObjectURL(file)
    setFilePreviewUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [file])

  const preview = previewUrl || filePreviewUrl || existingUrl || null
  const isExistingPreview = !previewUrl && !filePreviewUrl && !!existingUrl

  const handleFile = (selected) => {
    if (selected) onChange(selected)
  }

  const handleDrop = (event) => {
    event.preventDefault()
    setIsDragging(false)
    handleFile(event.dataTransfer.files?.[0])
  }

  return (
    <>
      <div
        className="border rounded d-flex flex-column align-items-center justify-content-center text-center p-4"
        style={{
          cursor: 'pointer',
          minHeight,
          backgroundColor: isDragging ? '#f0f4ff' : undefined,
          borderStyle: 'dashed',
        }}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <CIcon icon={cilCloudUpload} size="xl" className="text-primary mb-2" />
        <div>
          {title || (
            <>
              Drag and drop an image or <span className="text-primary">browse</span>
            </>
          )}
        </div>
        {hint && <div className="text-medium-emphasis small">{hint}</div>}
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept={accept}
          onChange={(event) => {
            handleFile(event.target.files[0])
            // Let the same file be picked again after it is cleared or replaced.
            event.target.value = ''
          }}
        />
      </div>
      {error ? (
        <CFormFeedback invalid className="d-block">
          {error}
        </CFormFeedback>
      ) : null}
      {preview && (
        <div className="mt-2">
          <img
            src={preview}
            alt={isExistingPreview ? 'Current' : 'New'}
            width={previewSize}
            height={previewSize}
            style={{ objectFit: 'cover', borderRadius: 6 }}
          />
        </div>
      )}
    </>
  )
}

export default ImageDropzone
